/**
 * Action Item Detail Modal
 * Full detail view with comments and edit functionality
 */
import { useState, useEffect } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { Loader2, Trash2, ExternalLink, User, Calendar, Tag } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { useToast } from '@/hooks/use-toast'
import {
  useActionItem,
  useUpdateActionItem,
  useArchiveActionItem,
  useAddComment,
} from '@/hooks/useActions'
import { useTeam } from '@/hooks/useTeam'
import { PRIORITY_COLORS } from '@/types/actions'

interface ActionItemDetailModalProps {
  actionId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ActionItemDetailModal({
  actionId,
  open,
  onOpenChange,
}: ActionItemDetailModalProps) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('')
  const [selectedPriority, setSelectedPriority] = useState('')
  const [selectedAssignee, setSelectedAssignee] = useState<number | null>(null)
  const [commentText, setCommentText] = useState('')
  
  const { toast } = useToast()
  const { data: actionItem, isLoading } = useActionItem(actionId || undefined)
  const updateMutation = useUpdateActionItem(actionId || '')
  const archiveMutation = useArchiveActionItem()
  const addCommentMutation = useAddComment(actionId || '')
  const { data: teamData } = useTeam()
  
  // Initialize form when data loads
  useEffect(() => {
    if (actionItem) {
      setTitle(actionItem.title)
      setDescription(actionItem.description || '')
      setSelectedStatus(actionItem.status)
      setSelectedPriority(actionItem.priority)
      setSelectedAssignee(actionItem.assignee?.id || null)
    }
  }, [actionItem])
  
  const handleSave = async () => {
    if (!actionId) return
    
    try {
      await updateMutation.mutateAsync({
        title,
        description,
        status: selectedStatus as any,
        priority: selectedPriority as any,
        assignee: selectedAssignee || undefined,
      })
      
      toast({
        title: 'Saved',
        description: 'Action item updated successfully.',
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update action item.',
        variant: 'destructive',
      })
    }
  }
  
  const handleAddComment = async () => {
    if (!commentText.trim() || !actionId) return
    
    try {
      await addCommentMutation.mutateAsync({ text: commentText })
      setCommentText('')
      
      toast({
        title: 'Comment added',
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to add comment.',
        variant: 'destructive',
      })
    }
  }
  
  const handleArchive = async () => {
    if (!actionId) return
    
    try {
      await archiveMutation.mutateAsync(actionId)
      toast({
        title: 'Archived',
        description: 'Action item has been archived.',
      })
      onOpenChange(false)
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to archive action item.',
        variant: 'destructive',
      })
    }
  }
  
  if (isLoading || !actionItem) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogTitle className="sr-only">Loading Action Item</DialogTitle>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </DialogContent>
      </Dialog>
    )
  }
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0">
        <div className="grid grid-cols-1 md:grid-cols-3 h-full">
          {/* Main Content */}
          <div className="md:col-span-2 flex flex-col">
            <DialogHeader className="p-6 pb-4">
              <DialogTitle className="sr-only">Edit Action Item</DialogTitle>
              <div className="space-y-4">
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="text-lg font-semibold"
                />
                
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="backlog">Backlog</SelectItem>
                    <SelectItem value="doing">Doing</SelectItem>
                    <SelectItem value="review">Review</SelectItem>
                    <SelectItem value="done">Done</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </DialogHeader>
            
            <ScrollArea className="flex-1 px-6">
              <div className="space-y-6 pb-6">
                {/* Description */}
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={6}
                    placeholder="Add a description..."
                  />
                </div>
                
                <Separator />
                
                {/* Comments */}
                <div className="space-y-4">
                  <h3 className="font-semibold">Comments</h3>
                  
                  {actionItem.comments && actionItem.comments.length > 0 ? (
                    <div className="space-y-4">
                      {actionItem.comments.map((comment) => (
                        <div key={comment.id} className="flex gap-3">
                          <div className="flex-shrink-0">
                            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                              <User className="h-4 w-4" />
                            </div>
                          </div>
                          <div className="flex-1 space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{comment.user.full_name}</span>
                              <span className="text-xs text-muted-foreground">
                                {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                              </span>
                            </div>
                            <p className="text-sm whitespace-pre-wrap">{comment.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No comments yet.</p>
                  )}
                  
                  {/* Add Comment */}
                  <div className="space-y-2">
                    <Textarea
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder="Add a comment..."
                      rows={3}
                    />
                    <Button
                      onClick={handleAddComment}
                      disabled={!commentText.trim() || addCommentMutation.isPending}
                      size="sm"
                    >
                      {addCommentMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Add Comment
                    </Button>
                  </div>
                </div>
              </div>
            </ScrollArea>
            
            {/* Footer Actions */}
            <div className="p-6 pt-4 border-t flex justify-between">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button onClick={handleSave} disabled={updateMutation.isPending}>
                {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Changes
              </Button>
            </div>
          </div>
          
          {/* Sidebar */}
          <div className="border-l bg-muted/30 p-6 space-y-6">
            <div className="space-y-4">
              {/* Priority */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  Priority
                </Label>
                <Select value={selectedPriority} onValueChange={setSelectedPriority}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">
                      <span style={{ color: PRIORITY_COLORS.low }}>● Low</span>
                    </SelectItem>
                    <SelectItem value="medium">
                      <span style={{ color: PRIORITY_COLORS.medium }}>● Medium</span>
                    </SelectItem>
                    <SelectItem value="high">
                      <span style={{ color: PRIORITY_COLORS.high }}>● High</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Assignee */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Assignee
                </Label>
                <Select
                  value={selectedAssignee?.toString() || 'unassigned'}
                  onValueChange={(value) =>
                    setSelectedAssignee(value === 'unassigned' ? null : parseInt(value))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {teamData?.members.map((member) => (
                      <SelectItem key={member.id} value={member.user.id.toString()}>
                        {member.user.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Reporter */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Reporter
                </Label>
                <div className="text-sm">{actionItem.reporter.full_name}</div>
              </div>
              
              {/* Dates */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Created
                </Label>
                <div className="text-sm text-muted-foreground">
                  {formatDistanceToNow(new Date(actionItem.created_at), { addSuffix: true })}
                </div>
              </div>
              
              {/* Linked Interaction */}
              {actionItem.interaction_details && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <ExternalLink className="h-4 w-4" />
                    Linked Interaction
                  </Label>
                  <Button variant="outline" size="sm" className="w-full justify-start" asChild>
                    <a href={`/app/interactions/${actionItem.interaction_details.ragflow_session_id}`} target="_blank">
                      {actionItem.interaction_details.visitor_name}
                      <ExternalLink className="ml-auto h-3 w-3" />
                    </a>
                  </Button>
                </div>
              )}
              
              {/* Labels */}
              {actionItem.labels && actionItem.labels.length > 0 && (
                <div className="space-y-2">
                  <Label>Labels</Label>
                  <div className="flex flex-wrap gap-1">
                    {actionItem.labels.map((label) => (
                      <Badge
                        key={label.id}
                        variant="secondary"
                        style={{
                          backgroundColor: `${label.color}20`,
                          color: label.color,
                          borderColor: label.color,
                        }}
                      >
                        {label.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <Separator />
            
            {/* Archive Button */}
            {actionItem.status === 'done' && !actionItem.is_archived && (
              <Button
                variant="destructive"
                className="w-full"
                onClick={handleArchive}
                disabled={archiveMutation.isPending}
              >
                {archiveMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="mr-2 h-4 w-4" />
                )}
                Archive
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
